/**
 * Référentiel Admin/RH - Standards d'Excellence BZ
 * Cadre de compétences pour les rôles Administration & RH (inspiré du référentiel Prime/ventes et du profil Naomie)
 * Structure alignée sur Directeur de Compte pour cohérence PRIsME
 */

export interface AdminRHSubCompetence {
  id: string;
  name: string;
  description: string;
  score: number;
  minRequired: number;
}

export interface AdminRHCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  minScore: number;
  currentScore: number;
  subCompetences: AdminRHSubCompetence[];
}

export interface AdminRHLevel {
  level: string;
  minScore: number;
  color: string;
  description: string;
}

export const adminRHLevels: AdminRHLevel[] = [
  {
    level: "Junior Prime Admin",
    minScore: 60,
    color: "#FFA500",
    description: "Niveau d'entrée - En développement opérationnel"
  },
  {
    level: "Prime Admin",
    minScore: 75,
    color: "#0D9488",
    description: "Niveau standard - Autonome et efficace"
  },
  {
    level: "Senior Prime Admin",
    minScore: 90,
    color: "#059669",
    description: "Niveau expert - Excellence et mentorat"
  }
];

export const adminRHCategories: AdminRHCategory[] = [
  {
    id: "operations-admin",
    name: "Opérations Administratives",
    icon: "FileText",
    color: "#0D9488",
    description: "Gestion des opérations administratives quotidiennes (recevables, facturation, contrats, conformité documentaire) avec rigueur, cadence et qualité.",
    minScore: 75,
    currentScore: 77,
    subCompetences: [
      { id: "recevables", name: "Gestion Recevables", description: "Suivi des recevables (factures, relances, encaissements, ajustements) et maintien d'un cashflow sain.", score: 80, minRequired: 75 },
      { id: "facturation", name: "Facturation", description: "Création/validation des factures, exactitude des informations, délais, gabarits et contrôles qualité.", score: 78, minRequired: 75 },
      { id: "contrats", name: "Gestion Contrats", description: "Classement, suivi d'échéances, conformité, versions, signatures et coordination des parties prenantes.", score: 75, minRequired: 70 },
      { id: "licences", name: "Gestion Licences", description: "Suivi des licences/outils, échéances, conformité, renouvellements et optimisation des coûts.", score: 74, minRequired: 70 },
      { id: "archivage", name: "Archivage & Classement", description: "Organisation documentaire, conventions de nommage, accès rapide et conformité aux standards internes.", score: 82, minRequired: 75 },
    ],
  },
  {
    id: "rh-admin",
    name: "RH & Administration du Personnel",
    icon: "Users",
    color: "#6C5CE7",
    description: "Soutien RH et administration du personnel: onboarding, dossiers employés, politiques, support paie/bénéfices et communication interne.",
    minScore: 75,
    currentScore: 74,
    subCompetences: [
      { id: "onboarding", name: "Onboarding", description: "Coordination d'arrivée, checklist, documents, accès, communication et expérience employé.", score: 76, minRequired: 70 },
      { id: "processus-rh", name: "Processus RH", description: "Gestion des processus RH récurrents (dossiers, demandes, suivi, conformité).", score: 75, minRequired: 70 },
      { id: "paie", name: "Support Paie", description: "Support administratif à la paie (collecte, validation, suivi des exceptions, communication).", score: 70, minRequired: 65 },
      { id: "benefices", name: "Bénéfices & Avantages", description: "Support et suivi des bénéfices/avantages, demandes, documentation et coordination.", score: 74, minRequired: 70 },
      { id: "policies", name: "Politiques RH", description: "Compréhension et application des politiques RH; aide à la vulgarisation et à la conformité.", score: 78, minRequired: 75 },
    ],
  },
  {
    id: "outils-systemes",
    name: "Outils & Systèmes",
    icon: "Database",
    color: "#1ABC9C",
    description: "Maîtrise des outils administratifs (Synapse, ERP, Germana, Office, CRM) pour exécuter efficacement et produire des livrables fiables.",
    minScore: 75,
    currentScore: 64,
    subCompetences: [
      { id: "synapse", name: "Synapse", description: "Utilisation de Synapse pour le suivi, la documentation et la gestion d'information administrative.", score: 65, minRequired: 70 },
      { id: "erp", name: "ERP BZ", description: "Navigation, saisies et suivis dans l'ERP (factures, données, états) avec rigueur.", score: 62, minRequired: 70 },
      { id: "germana", name: "Germana", description: "Prise en main progressive de Germana (bases, workflows, bonnes pratiques).", score: 55, minRequired: 60 },
      { id: "office", name: "Suite Office", description: "Maîtrise d'Excel/Word/Outlook/Teams pour produire des livrables et communiquer efficacement.", score: 83, minRequired: 80 },
      { id: "outils-crm", name: "Outils CRM", description: "Utilisation d'outils CRM pour suivi client, relances, notes et coordination interne.", score: 72, minRequired: 70 },
    ],
  },
  {
    id: "logistique",
    name: "Logistique & Coordination",
    icon: "Package",
    color: "#F39C12",
    description: "Coordination logistique et suivi (livraisons, inventaire, fournisseurs, planification) avec structure et communication.",
    minScore: 75,
    currentScore: 73,
    subCompetences: [
      { id: "livraisons", name: "Coordination Livraisons", description: "Planification, communication, suivi de livraison et gestion des imprévus.", score: 74, minRequired: 70 },
      { id: "inventaire", name: "Gestion Inventaire", description: "Suivi d'inventaire, réapprovisionnement, exactitude et contrôle.", score: 72, minRequired: 70 },
      { id: "fournisseurs", name: "Relations Fournisseurs", description: "Communication, suivi, résolution de problèmes et gestion d'attentes fournisseurs.", score: 76, minRequired: 72 },
      { id: "planification", name: "Planification Logistique", description: "Planifier, prioriser et coordonner les tâches/logistique avec une cadence fiable.", score: 72, minRequired: 70 },
    ],
  },
  {
    id: "communication",
    name: "Communication & Relation",
    icon: "MessageSquare",
    color: "#9B59B6",
    description: "Communication claire, service interne, coordination inter-équipes et capacité à créer un climat de confiance.",
    minScore: 75,
    currentScore: 82,
    subCompetences: [
      { id: "relation-client", name: "Relation Client Interne", description: "Support interne, traitement des demandes, qualité de service et suivi.", score: 80, minRequired: 75 },
      { id: "coordination", name: "Coordination Équipe", description: "Coordonner les parties prenantes, clarifier les attentes et tenir les suivis.", score: 82, minRequired: 75 },
      { id: "ecoute", name: "Écoute Active", description: "Écoute, reformulation, compréhension des besoins et résolution orientée action.", score: 85, minRequired: 80 },
      { id: "presentation", name: "Présentation & Réunions", description: "Animer/participer efficacement aux rencontres, synthétiser et faire avancer.", score: 78, minRequired: 72 },
    ],
  },
  {
    id: "rigueur-qualite",
    name: "Rigueur & Qualité",
    icon: "CheckCircle",
    color: "#E74C3C",
    description: "Rigueur, exactitude, contrôles, standards et amélioration continue pour réduire erreurs et frictions.",
    minScore: 75,
    currentScore: 85,
    subCompetences: [
      { id: "precision", name: "Précision", description: "Produire des livrables sans erreurs, attention aux détails et cohérence.", score: 88, minRequired: 80 },
      { id: "verification", name: "Vérification & Contrôle", description: "Mettre en place des contrôles et vérifier la qualité avant livraison.", score: 86, minRequired: 80 },
      { id: "standards", name: "Respect Standards", description: "Appliquer les standards internes, procédures et conventions.", score: 85, minRequired: 80 },
      { id: "amelioration", name: "Amélioration Continue", description: "Identifier et proposer des améliorations concrètes sur les processus.", score: 80, minRequired: 75 },
    ],
  },
  {
    id: "autonomie",
    name: "Autonomie & Initiative",
    icon: "Zap",
    color: "#3498DB",
    description: "Capacité à prendre en charge, prioriser, résoudre et apprendre de manière autonome, avec escalade intelligente.",
    minScore: 75,
    currentScore: 77,
    subCompetences: [
      { id: "proactivite", name: "Proactivité", description: "Anticiper, proposer et prendre en charge des actions utiles sans attendre.", score: 78, minRequired: 72 },
      { id: "resolution", name: "Résolution Problèmes", description: "Diagnostiquer, structurer la résolution et documenter les apprentissages.", score: 76, minRequired: 72 },
      { id: "prise-decision", name: "Prise de Décision", description: "Prendre des décisions dans son périmètre avec jugement et escalade au besoin.", score: 75, minRequired: 70 },
      { id: "apprentissage", name: "Apprentissage Autonome", description: "Apprendre, pratiquer, et renforcer la maîtrise via répétition et feedback.", score: 78, minRequired: 72 },
    ],
  },
  {
    id: "collaboration",
    name: "Collaboration & Travail Équipe",
    icon: "Users",
    color: "#2ECC71",
    description: "Collaboration, entraide, partage de connaissances et posture constructive dans l'équipe.",
    minScore: 75,
    currentScore: 82,
    subCompetences: [
      { id: "travail-equipe", name: "Travail d'Équipe", description: "Travailler efficacement en équipe, fiabilité, coordination et respect des engagements.", score: 85, minRequired: 80 },
      { id: "partage", name: "Partage Connaissances", description: "Partager les meilleures pratiques, documenter et aider l'équipe à monter en compétence.", score: 78, minRequired: 72 },
      { id: "support", name: "Support Collègues", description: "Soutenir les collègues, répondre, guider et débloquer rapidement.", score: 84, minRequired: 78 },
      { id: "mediation", name: "Médiation & Conciliation", description: "Gérer les frictions, clarifier et trouver une solution constructive.", score: 78, minRequired: 70 },
    ],
  },
];
